import { getAgentUrl } from "src/util/url";

const EVENT_TYPE = {
  ON_MESSAGE_CHUNK: "message_chunk",
  ON_MESSAGE: "message",
  ON_CURRENT_TOOL_USE: "current_tool_use",
  GENERIC_EVENT: "event",
  STREAM_COMPLETE: "stream_complete",
  PIPELINE_STEP: "pipeline_step",
  PIPELINE_COMPLETE: "pipeline_complete",
  PIPELINE_CANCELLED: "pipeline_cancelled",
  DASHBOARD_PERSISTED: "dashboard_persisted",
  CONNECTION_ESTABLISHED: "connection_established",
  TEMPLATE_CHUNK: "template_chunk",
};

const streamAIResponse = async (
  params,
  {
    tenantId,
    apiUrl,
    onMessageStart,
    onMessage,
    onMessageChunk,
    onCurrentToolUse,
    onPipelineStep,
    onPipelineComplete,
    onPipelineCancelled,
    onDashboardPersisted,
    onTemplateChunk,
    onError,
    onDone,
    headers = {},
    agentName,
    signal,
  }
) => {
  try {
    const response = await fetch(getAgentUrl({ tenantId, apiUrl, agentName }), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: headers?.Authorization,
        "x-workspace-id": headers?.["x-workspace-id"],
      },
      body: JSON.stringify({
        ...params,
        headers,
        stream: true,
      }),
      signal,
    });

    if (!response.ok) {
      const retryAfter = response.headers.get("Retry-After");
      // Read the body once as text — calling .json() then .text() throws
      // "body stream already read" for non-JSON error responses.
      const rawBody = await response.text();
      let errorBody;
      try {
        errorBody = JSON.parse(rawBody);
      } catch {
        errorBody = { message: rawBody };
      }
      onError({
        status: response.status,
        retryAfter: retryAfter ? parseInt(retryAfter, 10) : null,
        message:
          errorBody?.message || `Request failed with status ${response.status}`,
        ...errorBody,
      });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    let complete = false;

    while (!complete) {
      const { done, value } = await reader.read();

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          let data;
          try {
            data = JSON.parse(line.replace("data: ", ""));
          } catch {
            // Partial chunk boundary or non-JSON keepalive — skip this frame
            // instead of letting one bad line kill the whole stream.
            continue;
          }

          // pipeline_step events use `error` for retry/fail detail — not a stream-level fatal.
          const isFatalStreamError =
            data?.type === "error" ||
            (Boolean(data?.error) && data?.type !== EVENT_TYPE.PIPELINE_STEP);

          if (isFatalStreamError) {
            onError(data);
            complete = true;
            // Stop here: a fatal frame must not also run the normal type
            // dispatch (e.g. a pipeline_cancelled payload carrying a
            // top-level `error` would otherwise fire onPipelineCancelled too).
            continue;
          }

          if (
            data?.type === EVENT_TYPE.GENERIC_EVENT &&
            data?.event?.messageStart?.role === "assistant"
          ) {
            onMessageStart?.(data);
          }

          if (data?.type === EVENT_TYPE.ON_MESSAGE_CHUNK) {
            onMessageChunk?.(data);
          } else if (data?.type === EVENT_TYPE.ON_MESSAGE) {
            const raw = data.final_message ?? data.message;
            const final_message = raw
              ? {
                  ...raw,
                  id: raw.id ?? `temp-assistant-${Date.now()}`,
                  aiChatId: raw.aiChatId ?? params.aiChatId,
                }
              : null;
            onMessage?.({ ...data, final_message });
          } else if (data?.type === EVENT_TYPE.ON_CURRENT_TOOL_USE) {
            onCurrentToolUse?.(data);
          } else if (data?.type === EVENT_TYPE.PIPELINE_STEP) {
            onPipelineStep?.(data);
          } else if (
            data?.type === EVENT_TYPE.PIPELINE_COMPLETE ||
            data?.type === "pipeline_completed"
          ) {
            onPipelineComplete?.(data);
          } else if (data?.type === EVENT_TYPE.PIPELINE_CANCELLED) {
            onPipelineCancelled?.(data);
          } else if (data?.type === EVENT_TYPE.DASHBOARD_PERSISTED) {
            onDashboardPersisted?.(data);
          } else if (data?.type === EVENT_TYPE.TEMPLATE_CHUNK) {
            onTemplateChunk?.(data);
          } else if (data?.type === EVENT_TYPE.STREAM_COMPLETE) {
            onDone();
            complete = true;
          }
        }
      }

      if (done && !complete) {
        onError("Stream ended early.");
        complete = true;
      }
    }
  } catch (e) {
    if (signal?.aborted) {
      return;
    }
    onError(e);
  }
};

export default streamAIResponse;
